import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient from '@/services/apiClient'
import { AUTH_LOGIN, AUTH_LOGOUT } from '@/services/apiEndpoints'
import { log } from '@/utils/logger'
import { setAuthenticatedUser, clearAuthenticatedUser } from '@/services/monitoringService'

interface AuthUser {
  email: string
  officeId: string
  userId: number
  role: string
}

interface AuthState {
  isAuthenticated: boolean
  user: AuthUser | null
  login: (email: string, officeId: string, password: string) => Promise<{ success: boolean; error?: string; code?: string }>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,

      login: async (email, officeId, password) => {
        log.info('login attempt', { officeId })
        try {
          const res = await apiClient.post(
            AUTH_LOGIN,
            { email: email.trim(), officeId: officeId.trim(), password },
            { withCredentials: true }
          )
          const { accessToken, userEmail, userId, role, officeId: userOfficeId } = res.data.data
          localStorage.setItem('auth_token', accessToken)
          set({
            isAuthenticated: true,
            user: { email: userEmail, userId, role, officeId: userOfficeId },
          })
          setAuthenticatedUser(userEmail)
          log.info('login success', { userId: String(userId), role, officeId: userOfficeId })
          return { success: true }
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { code?: string } } }
          const code = axiosErr?.response?.data?.code
          log.warn('login failed', { code: code ?? 'UNKNOWN', officeId })
          return { success: false, code }
        }
      },

      logout: async () => {
        log.info('logout')
        try {
          await apiClient.post(AUTH_LOGOUT, {}, { withCredentials: true })
        } catch { /* fire-and-forget */ }
        localStorage.removeItem('auth_token')
        clearAuthenticatedUser()
        set({ isAuthenticated: false, user: null })
      },
    }),
    {
      name: 'vvsi-auth',
      onRehydrateStorage: () => (state) => {
        if (state?.isAuthenticated && state.user) {
          setAuthenticatedUser(state.user.email)
        }
      },
    }
  )
)
